import { RoseTree } from '@tableau/data-structures';
import { NestedProjectBasicInfo, NestedProjectExplorer, NestedProjectsFractalRowStore, NestedProjectsTreeHelper } from '@tableau/nested-project-explorer';
import * as _ from 'underscore';
import { Messages } from '../../codegen/Localize';
import {
  createMultipleItemResultNotificationValues,
  MultipleItemResultNotificationValues
} from '../../commonInfra/contentActions/ActionMessages';
import { ContentActionNotification } from '../../commonInfra/contentActions/ContentActionNotification';
import {
  IErrorItem, MultipleItemResult
} from '../../commonInfra/contentActions/MultipleItemResult';
import { openReactAlertDialog } from '../../commonInfra/dialogs/ReactAlertDialog';
import { DataSourceErrors } from '../../content/services/DataSourceErrors';
import { ProjectErrors } from '../../content/services/ProjectErrors';
import { FlowErrors } from '../../flow/services/FlowErrors';
import { DestinationProjectContentType, DestinationProjects } from '../../projects/resources/DestinationProjects';
import { Projects } from '../../projects/resources/Projects';
import { IActionContext } from '../helpers/ActionContext';
import { SliceableHelper } from '../helpers/SliceableHelper';
import { L10n } from '../L10n/L10n';
import { DestinationProjectMapper } from '../mappers/DestinationProjectMapper';
import {
  DataRoleContentType,
  DatasourceContentType,
  FlowContentType,
  IContentType,
  LensContentType,
  ProjectContentType,
  WorkbookContentType
} from '../models/ContentTypes';
import { FlowBaseInfo, FlowListItemInfo } from '../models/FlowInfo';
import { IDataRoleBase } from '../models/IDataRoleInfo';
import { IDataSourceBase } from '../models/IDataSourceInfo';
import { IProjectBase } from '../models/IProjectInfo';
import { ISliceable } from '../models/ISliceable';
import { IWorkbookBase } from '../models/IWorkbookInfo';
import { INamedItemInfo } from '../models/resourceInfoParts';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { DataRoles } from '../resources/Dataroles';
import { DataSources } from '../../content/resources/Datasources';
import { Flows } from '../resources/Flows';
import { Workbooks } from '../../content/resources/Workbooks';
import { DestinationProjectStatus } from '../ServerApi/types/IDestinationProject';
import { IProjectName } from '../ServerApi/types/IProject';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { DataRoleErrors } from './DataRoleErrors';
import { WorkbookErrors } from './WorkbookErrors';
import { ILensBase } from '../models/ILensInfo';
import { Lenses } from '../resources/Lenses';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';

interface ITranslationIds {
  promptId: string;
  successId: string;
  errorId: string;
  dialogTitle: string;
  confirmationText: string;
}

export interface IConfirmMoveToProjectScope extends ng.IScope {
  prompt: string;
  projectPicker: {
    projectsQuery: (query: string) => ISliceable<IProjectName>;
    targetProject: IProjectName;
  };
  projectExplorer: {
    props: NestedProjectExplorer.Props | null;
    selectedProject: NestedProjectBasicInfo | null;
  };
}

export interface IResultWithDestinationProject extends IResult {
  destinationProjectId: string;
}

export class MoveToProjectAction {

  public static $inject = [
    'ConfirmActionDialog',
    '$translate',
    'WorkbookErrors'
  ];

  constructor(
    private ConfirmActionDialog: ConfirmActionDialog,
    private $translate: ng.translate.ITranslateService,
    private WorkbookErrors: WorkbookErrors) {
  }

  public moveProjects(projects: Set<IProjectBase>, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.move(
      {
        promptId: 'selectProjectForProjects',
        successId: 'moveProjectsToProjectSuccess',
        errorId: 'moveProjectsToProjectError',
        dialogTitle: 'moveProject',
        confirmationText: 'confirmMoveProject'
      },
      projects,
      ProjectContentType,
      (projectId: string) => Projects.moveToProject(projectId, projects.keys()),
      actionContext);
  }

  public moveWorkbooks(workbooks: Set<IWorkbookBase>, actionContext: IActionContext): ng.IPromise<IResultWithDestinationProject> {
    return this.move(
      {
        promptId: 'selectProjectForWorkbooks',
        successId: 'moveWorkbooksToProjectSuccess',
        errorId: 'moveWorkbooksToProjectError',
        dialogTitle: 'moveToProject',
        confirmationText: 'confirmMoveToProject'
      },
      workbooks,
      WorkbookContentType,
      (projectId: string) => Workbooks.moveToProject(projectId, workbooks.keys()),
      actionContext);
  }

  public moveDatasources(datasources: Set<IDataSourceBase>, actionContext: IActionContext): ng.IPromise<IResult> {

    // Disallow move if all selected datasources are embedded.
    if (datasources.all((ds: IDataSourceBase) => _.isObject(ds.workbook))) {
      return openReactAlertDialog({
        title: Messages.moveDatasourcesToProject_cannotMoveAlertTitle(),
        message: Messages.moveDatasourcesToProject_cannotMoveAlertMessage()
      }).result;
    }

    return this.move(
      {
        promptId: 'selectProjectForDatasources',
        successId: 'moveDatasourcesToProjectSuccess',
        errorId: 'moveDatasourcesToProjectError',
        dialogTitle: 'moveToProject',
        confirmationText: 'confirmMoveToProject'
      },
      datasources,
      DatasourceContentType,
      (projectId: string) => DataSources.moveToProject(projectId, datasources.keys()),
      actionContext);
  }

  public moveFlows(flows: Set<FlowListItemInfo>, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.move(
      {
        promptId: 'selectProjectForFlows',
        successId: 'moveFlowsToProjectSuccess',
        errorId: 'moveFlowsToProjectError',
        dialogTitle: 'moveToProject',
        confirmationText: 'confirmMoveToProject'
      },
      flows,
      FlowContentType,
      (projectId: string) => Flows.moveToProject(projectId, flows.keys()),
      actionContext);
  }

  public moveDataRoles(dataRoles: Set<IDataRoleBase>, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.move(
      {
        promptId: 'selectProjectForDataRoles',
        successId: 'moveDataRolesToProjectSuccess',
        errorId: 'moveDataRolesToProjectError',
        dialogTitle: 'moveToProject',
        confirmationText: 'confirmMoveToProject'
      },
      dataRoles,
      DataRoleContentType,
      (projectId: string) => DataRoles.moveToProject(projectId, dataRoles.keys()),
      actionContext);
  }

  public moveLenses(lenses: Set<ILensBase>, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.move(
      {
        promptId: 'selectProjectForLenses',
        successId: 'moveLensesToProjectSuccess',
        errorId: 'moveLensesToProjectError',
        dialogTitle: 'moveToProject',
        confirmationText: 'confirmMoveToProject'
      },
      lenses,
      LensContentType,
      (projectId: string) => Lenses.moveToProject(projectId, lenses.keys()),
      actionContext);
  }

  private move(
    translations: ITranslationIds,
    items: Set<INamedItemInfo>,
    itemType: IContentType,
    moveFn: (projectId: string) => ng.IPromise<any>,
    actionContext: IActionContext): ng.IPromise<IResultWithDestinationProject> {

    const newDialogScope = <IConfirmMoveToProjectScope>actionContext.scope.$new();
    this.initNestedProjectExplorer(newDialogScope, itemType, items.map(item => item.id));

    newDialogScope.prompt = this.$translate.instant(translations.promptId, {
      ITEM_COUNT: items.size(),
      ITEM_NAME: L10n.elideIfLong(items.first().name)
    }, L10n.Interpolations.Count);

    const dialogInstance = this.ConfirmActionDialog.open({
      scope: newDialogScope,
      title: this.$translate.instant(translations.dialogTitle, {
        ITEM_COUNT: items.size()
      }, L10n.Interpolations.Count),
      bodyTemplateUrl: 'moveToProjectDialog.html',
      actionName: this.$translate.instant(translations.confirmationText, {
        ITEM_COUNT: items.size()
      }, L10n.Interpolations.Count),
      action: () => {
        const destinationProjectId = this.getDestinationProjectIdFromDialog(newDialogScope);
        return moveFn(destinationProjectId);
      },
      confirmDisabled: () => {
        return newDialogScope.projectExplorer.selectedProject == null;
      }
    });

    dialogInstance.result.then((result: IResult) => {
      ContentActionNotification.displayItemizedToastsForResult(
        this.notification(
          new MultipleItemResult(items, result),
          translations,
          itemType,
          newDialogScope.projectExplorer.selectedProject
        )
      );
    });

    return dialogInstance.result
      .then((result: IResultWithDestinationProject) => {
        return {
          ...result,
          destinationProjectId: this.getDestinationProjectIdFromDialog(newDialogScope)
        };

      })
      .finally(() => newDialogScope.$destroy());
  }

  private getDestinationProjectIdFromDialog(dialogScope: IConfirmMoveToProjectScope): string {
    const destinationProjectId = dialogScope.projectExplorer.selectedProject.id;
    return (destinationProjectId === NestedProjectsFractalRowStore.SentinelSiteId ? null : destinationProjectId);
    // API expects a null ID if we're moving a project to the top level of the site
  }

  private initNestedProjectExplorer(scope: IConfirmMoveToProjectScope, itemType: IContentType, itemIds: string[]) {
    // Search queries to be implemented in TFS 645286
    this.getAllProjectsAsTrees('', itemType, itemIds).then(projectTrees => {
      // No scope.$apply here because it's called from within an ng.IPromise callback
      scope.projectExplorer.props = {
        siteName: getSessionManagementServiceInstance().sessionSiteName(),
        topLevelDestinationStatus: this.getTopLevelDestinationStatus(itemType),
        projects: projectTrees,
        onSelectedProjectChanged: (project: NestedProjectBasicInfo | null) =>
          scope.$apply(() => scope.projectExplorer.selectedProject = project)
      };
    });

    scope.projectExplorer = {
      props: null,
      selectedProject: null
    };
  }

  private getTopLevelDestinationStatus(itemType: IContentType): DestinationProjectStatus {
    if (itemType !== ProjectContentType) {
      return 'structurally_invalid';
    } else if (!getSessionManagementServiceInstance().isSessionSiteOrServerAdmin()) {
      return 'insufficient_permissions';
    } else {
      return 'valid';
    }
  }

  // This method is separate so it can be mocked in unit tests
  public getAllProjectsAsTrees(
    query: string,
    itemType: IContentType,
    itemIds: string[],
  ): ng.IPromise<RoseTree<NestedProjectBasicInfo>[]> {
    const contentType = <DestinationProjectContentType>itemType.name;
    const projectsQuery = DestinationProjects.getQueryForSpecificSourceContentAndMatchText(contentType, itemIds,
      query);
    return SliceableHelper.fetchAll(projectsQuery).then(projects => {
      const destinationProjects = DestinationProjectMapper.map(projects.items);
      return NestedProjectsTreeHelper.convertProjectListToTrees(destinationProjects);
    });
  }

  private notification(
    result: MultipleItemResult<INamedItemInfo>,
    translations: ITranslationIds,
    itemType: IContentType,
    project: IProjectName): MultipleItemResultNotificationValues {

    return createMultipleItemResultNotificationValues(result,
      {
        describeSuccessSummary: (successCount: number) =>
          this.$translate.instant(translations.successId, {
            ITEM_COUNT: successCount,
            PROJECT_NAME: L10n.elideIfLong(project.name)
          }, L10n.Interpolations.Count),

        describeErrorSummary: (errorCount: number) =>
          this.$translate.instant(translations.errorId, {
            ERROR_COUNT: errorCount,
            PROJECT_NAME: L10n.elideIfLong(project.name)
          }, L10n.Interpolations.Count),

        describeErrorDetail: (errorItem: IErrorItem<INamedItemInfo>) => {
          if (!errorItem.item || !errorItem.error) return;
          if (itemType === ProjectContentType) {
            return ProjectErrors.errorDetailsFor(<IErrorItem<IProjectBase>>errorItem);
          } else if (itemType === DatasourceContentType) {
            return DataSourceErrors.errorDetailsFor(<IErrorItem<IDataSourceBase>>errorItem);
          } else if (itemType === WorkbookContentType) {
            return this.WorkbookErrors.errorDetailsFor(<IErrorItem<IWorkbookBase>>errorItem);
          } else if (itemType === FlowContentType) {
            return FlowErrors.errorDetailsFor(<IErrorItem<FlowBaseInfo>>errorItem);
          } else if (itemType === DataRoleContentType) {
            return DataRoleErrors.errorDetailsFor(<IErrorItem<IDataRoleBase>>errorItem);
          }
        }
      }
    );
  }

}

Module.instance().service('MoveToProjectAction', MoveToProjectAction);
